import * as React from 'react';
import { createPortal } from 'react-dom';
import { Collapse, Row } from 'reactstrap'
import classNames from 'classnames';

import { accentUtils } from '../../services/HelperService';
import { configFrom, getProduct, optionDisplayTypes } from '../../services/ProductService';
import { getAccess } from '../../services/UserService';
import { isMobileOnly } from '../AccentDisplay';
import { hasRuntimeVisualization, readRuntimeVisualization } from './runtimeVisualization';

import { ConfigActionButton } from './ConfigActionButton';
import { ConfigCheckbox } from './ConfigCheckbox';
import { ConfigDecimal } from './ConfigDecimal';
import { ConfigExtras } from './ConfigExtras';
import { ConfigLength } from './ConfigLength';
import { ConfigNumeric } from './ConfigNumeric';
import { ConfigPanelPattern } from './ConfigPanelPattern';
import { ConfigPicklist } from './ConfigPicklist';
import { ConfigSingleLineFabric } from './ConfigSingleLineFabric';
import { ConfigSizeAllowed } from './ConfigSizeAllowed';
import { ConfigSizeSingle } from './ConfigSizeSingle';
import { ConfigText } from './ConfigText';
import { ConfigTwoLineFabric } from './ConfigTwoLineFabric';
import { ConfiguratorContext } from './ConfigContextProvider';

import './ProductConfigurator.css';
import { ActionBar, ConfigDebugPanel, ConfigHelpNote, ConfigLabel, ConfiguratorIssueSummary, focusConfiguratorIssue, getSizeRequirements, isHiddenWhenDisabled, isOptionVisible, isReadOnly, momentaryButtonDisplayType, SizeIndicator, useChanged, useConfiguratorIssues, useVisibleForOrder } from './ProductConfiguratorShared';

import { ConfigSizeAction } from './SelectSizeDlg';

export const VerticalConfiguratorCtrl = React.memo(props => {

    const ctx = React.useContext(ConfiguratorContext)
    const container = React.useRef(null);
    const [expandAll, setExpandAll] = React.useState({ expanded: true });
    const issues = useConfiguratorIssues();
    React.useEffect(() => {
        props.onValidationIssuesChanged?.(issues);
    }, [issues, props.onValidationIssuesChanged]);

    const groups = configFrom(ctx.config.GetActiveGroupsForOrder())
        .where(g => !g.MultiProductOption)
        .select(g => <Group className="options" key={`key_${g.ID}`} group={g} expandAll={expandAll} issues={issues} />).toArray();

    const extras = props.hideExtras ? null : configFrom(ctx.config.GetActiveGroupsForOrder())
        .where(g => g.MultiProductOption)
        .select(g => <Group className="extras" group={g} key={`key_${g.ID}`} expandAll={expandAll} issues={issues} />).toArray();

    React.useEffect(() => {
        ctx.config.ValidateOptions(true);
    }, [ctx.config]);

    const configContent = (
        <div className="config-vertical" ref={container}>
            {!props.hideValidationSummary && <ConfiguratorIssueSummary issues={issues} onGoToField={issue => focusConfiguratorIssue(container.current, issue)} />}
            <div className="config-group-tools">
                <ViewIn3DButton />
                <button type="button" className="config-text-button" onClick={() => setExpandAll({ expanded: true })}>Expand all</button>
                <button type="button" className="config-text-button" onClick={() => setExpandAll({ expanded: false })}>Collapse all</button>
            </div>
            {!props.hideSize && <div className="config-group-container">
                <SizeGroup expandAll={expandAll} issues={issues} />
            </div>}
            <div className="config-group-container">
                {groups}
            </div>
            {!props.hideExtras && < div className="config-extras-container">
                {extras}
            </div>}
        </div>
    );

    if (!ctx.debugPanelVisible) {
        return configContent;
    }

    // The page supplied a place for the debug panel (the job line layout puts it between
    // the options and the pricing sidebar). Context flows through the portal, so the panel
    // still reads this configurator. null means the host is mounting: render nothing there
    // yet rather than flashing the inline split for a frame.
    if (ctx.debugPanelHost !== undefined) {
        return <>
            {configContent}
            {ctx.debugPanelHost && createPortal(<ConfigDebugPanel />, ctx.debugPanelHost)}
        </>;
    }

    // Stand-alone configurator (e.g. the product test view): split the available width.
    // Both halves shrink, so the pair never grows past the column they are in.
    return (
        <div className="config-debug-layout">
            <div className="config-debug-layout-options">
                {configContent}
            </div>
            <div className="config-debug-layout-panel">
                <ConfigDebugPanel />
            </div>
        </div>
    );
});

/**
 * "View in 3D": opens the product's model at this line's size and selections. Only when the module is licensed and
 * the product (the multi product first, else this line's product) carries the model in its runtime data (published
 * with the product, so a marketplace copy renders too) - a read-only line can be viewed too, since its config is
 * loaded like any other.
 */
const ViewIn3DButton = () => {
    const ctx = React.useContext(ConfiguratorContext);
    const multi = ctx.getMultiConfigManager?.() ?? null;
    const multiVisualization = multi?.getVisualization?.() ?? null;
    const visualization = multiVisualization ?? readRuntimeVisualization(ctx.config);

    if (!hasRuntimeVisualization(visualization) || !getAccess()?.modules?.().RTL_ProductVisualization?.()) return null;

    const open = async () => {
        // the dialog, the binding resolver, the model runtime and three.js all load on this click, never with the configurator
        const [{ showOrderLineModelDlg }] = await Promise.all([import('./OrderLineModelDlg'), ctx.waitWhileInProgress?.()]);
        const productName = getProduct(ctx.productID)?.ProductName;
        showOrderLineModelDlg({
            visualization,
            config: ctx.config,
            orderLine: ctx.orderLine,
            members: multiVisualization ? (multi.getMemberConfigs?.() ?? []) : [],
            baseImagePath: ctx.baseImagePath,
            title: [ctx.orderLine?.LocationOther, productName].filter(x => !accentUtils.isEmpty(x)).join(" - "),
        });
    };

    // "3D Photo": the same model over the customer's photo, saved as a job document. Needs a saved line of a job
    // (the document belongs to the job and the photo's windows are its lines); not for phones - the corners are
    // placed with a pointer / stylus on a tablet or desktop.
    const openPhoto = async () => {
        const [{ showPhotoVisualizationDlg }] = await Promise.all([import('./photo/PhotoVisualizationDlg'), ctx.waitWhileInProgress?.()]);
        showPhotoVisualizationDlg({
            jobID: ctx.orderLine.JobID,
            lineJobLineID: ctx.orderLine.JobLineID,
            liveLine: { config: ctx.config, orderLine: ctx.orderLine, productID: ctx.productID, baseImagePath: ctx.baseImagePath, isReadOnly: ctx.isReadOnly, notifyDirty: ctx.notifyDirty },
        });
    };
    const canPhoto = !isMobileOnly && !multiVisualization && ctx.orderLine?.ID > 0 && ctx.orderLine?.JobID > 0 && !accentUtils.isEmpty(ctx.orderLine?.JobLineID);

    return <>
        <button type="button" className="config-text-button" data-tagname="viewIn3D" onClick={open} style={{ marginRight: canPhoto ? undefined : "auto" }}>View in 3D</button>
        {canPhoto && <button type="button" className="config-text-button" data-tagname="photo3D" onClick={openPhoto} style={{ marginRight: "auto" }}>3D Photo</button>}
    </>;
};

export const ConfigGroupPanel = ({ title, summary, issues = [], expandAll, hidden, className, children }) => {
    const [expanded, setExpanded] = React.useState(true);
    const container = React.useRef(null);
    const bodyID = React.useId();
    // Apply group commands before a following Go-to action can arrive. A passive
    // effect can otherwise apply an older Collapse-all command after focus opens it.
    React.useLayoutEffect(() => {
        if (expandAll) setExpanded(expandAll.expanded);
    }, [expandAll]);
    React.useLayoutEffect(() => {
        const element = container.current;
        const expand = () => setExpanded(true);
        element.addEventListener("config-focus-field", expand);
        return () => element.removeEventListener("config-focus-field", expand);
    }, []);
    const hasError = issues.some(issue => issue.level === "error");
    const status = hasError ? "Needs attention" : issues.length ? "Has warnings" : "No issues";
    return <section className={`config-group ${className || ""}`} hidden={hidden} ref={container}>
        <button type="button" className="config-group-header" aria-expanded={expanded} aria-controls={bodyID} onClick={() => setExpanded(value => !value)}>
            <span className="config-group-chevron" aria-hidden="true">{expanded ? "⌄" : "›"}</span>
            <span className="config-group-title">{title}</span>
            {summary && <span className="config-group-summary">{summary}</span>}
            <span className={classNames("config-group-status", { error: hasError, warning: !hasError && issues.length > 0 })} aria-label={status}>
                {issues.length ? "!" : "✓"}
            </span>
        </button>
        <Collapse isOpen={expanded} id={bodyID}>{children}</Collapse>
    </section>;
};

const SizeGroup = props => {
    const ctx = React.useContext(ConfiguratorContext);

    const productSize = ctx.config.Data.GetSize();
    const sizeReqs = productSize ? getSizeRequirements(productSize) : null;
    const isHidden = sizeReqs && (sizeReqs.requireWidth || sizeReqs.requireHeight);

    return (
        <ConfigGroupPanel title="Size" className="size" hidden={!isHidden} expandAll={props.expandAll}
            issues={props.issues.filter(issue => issue.field?.startsWith("size:"))}
            summary={[sizeReqs?.requireWidth && ctx.config.OrderLine.Width?.toString(), sizeReqs?.requireHeight && ctx.config.OrderLine.Height?.toString()].filter(Boolean).join(" × ")}>
            <Size productSize={productSize} sizeReqs={sizeReqs} />
        </ConfigGroupPanel>
    );
};

const Size = ({ productSize, sizeReqs }) => {
    if (!productSize || !sizeReqs) return null;

    const width = sizeReqs.requireWidth
        ? <SizeWidth productSize={productSize} requireMulti={sizeReqs.requireMultiWidth || sizeReqs.requireMultiHeight} />
        : null;

    const height = sizeReqs.requireHeight
        ? <SizeHight productSize={productSize} requireMulti={sizeReqs.requireMultiWidth || sizeReqs.requireMultiHeight} />
        : null;

    return (
        <div className="config-size-container">
            {width}
            {height}
        </div>
    );
};

const SizeWidth = props => {
    return (
        <div className="config-size" data-config-field="size:width" tabIndex={-1}>
            <div className="config-size-header">
                <SizeIndicator width />
                <div className="config-size-label">Width</div>
            </div>
            <SizeCtrl width allowedValuesField="AllowedWidthList" productSize={props.productSize} requireMulti={props.requireMulti} />
        </div>
    );
};

const SizeHight = props => {
    return (
        <div className="config-size" data-config-field="size:height" tabIndex={-1}>
            <div className="config-size-header">
                <SizeIndicator />
                <div className="config-size-label">Height</div>
            </div>
            <SizeCtrl allowedValuesField="AllowedHeightList" productSize={props.productSize} requireMulti={props.requireMulti} />
        </div>
    );
};

export const SizeCtrl = React.memo(props => {

    const fixedValues = React.useMemo(() => accentUtils.isEmpty(props.productSize[props.allowedValuesField]) ? [] : JSON.parse(props.productSize[props.allowedValuesField]), [props.productSize]);
    const isFixed = fixedValues.length !== 0;

    const className = classNames("config-size-value-container", { "small": props.small, "multi": props.requireMulti });

    return (
        <div className={className}>
            {!isFixed && <ConfigSizeSingle width={props.width} readOnly={props.requireMulti} isMulti={props.requireMulti} />}
            {isFixed && <ConfigSizeAllowed width={props.width} readOnly={props.requireMulti} sizes={fixedValues} small={props.small} />}
            <div className="config-size-value-actions">
                {props.requireMulti && <ConfigSizeAction width={props.width} />}
            </div>
        </div>
    );
});

export function getOptionSummary(model) {
    if (!model || model.CurrentValueIsNullOrEmpty) return null;
    const selection = model.IsPicklist ? model.GetProductOptionValue(model.OrderLineOption?.ProductOptionValueID) : null;
    // CustomValue is a picklist override accessor, not a generic display value.
    // Linked runtimes throw when it is read for length options.
    const value = selection?.Description || (model.IsPicklist ? model.CustomValue : null) || model.CurrentValueAsString;
    // Fabric and other custom controls may expose identifiers rather than text.
    return !value || /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value) ? null : value;
}

const Group = props => {

    const ctx = React.useContext(ConfiguratorContext)
    const group = props.group;

    // Runtime (show/hide engine) visibility for the whole group. Only re-renders on actual flips.
    const groupVisibleForOrder = useVisibleForOrder(
        () => ctx.config.IsGroupVisibleForOrder(group),
        `Group_${group.ID}`);

    // Get the options included in the Group
    const initialOptions = React.useMemo(() => {
        return group.MultiProductOption
            ? []
            : configFrom(ctx.config.GetActiveProductOptionsForOrder(group.ID)).toArray();
    }, [ctx.config, ctx.refreshKey, group.ID, group.MultiProductOption]);

    // Initialise a visibility map based on the starting visibility of each child (Option)
    const initialVisibilityMap = React.useMemo(() => {
        const map = {};
        for (const option of initialOptions) {
            const model = ctx.config.GetOptionModel(option.ID);
            // Same test the child Option applies, so the group's first render agrees
            // with what its options report back.
            map[option.ID] = !isHiddenWhenDisabled(option, model) && isOptionVisible(model, ctx.filter);
        }
        return map;
    }, [ctx, initialOptions])

    // Create a visibility map that will dynamically update
    const [visibilityMap, setVisibilityMap] = React.useState(initialVisibilityMap);

    // Callback for child Options to report their current visibility
    const visibility = React.useCallback((id, visible) => {
        setVisibilityMap(prev => {
            if (prev[id] === visible) return prev;
            return { ...prev, [id]: visible };
        });
    }, []);

    // Create the Options element(s) for display
    const options = initialOptions.map(o => 
        <Option key={`key_${o.ID}`} option={o} visibility={visibility}/>
    );

    // Check if the Group should be hidden
    const isHidden = !groupVisibleForOrder
        || (group.MultiProductOption
            ? false
            : ((initialOptions.length > 0 && initialOptions.every(o => visibilityMap[o.ID] === false))
                || initialOptions.length === 0
            ));

    const issues = props.issues.filter(issue => initialOptions.some(option => option.ID.toString().toUpperCase() === issue.field));
    const summary = initialOptions.filter(option => visibilityMap[option.ID] !== false)
        .map(option => getOptionSummary(ctx.config.GetOptionModel(option.ID)))
        .filter(Boolean).slice(0, 3).join(" · ");

    return (
        <ConfigGroupPanel title={group.Description} summary={summary} className={props.className} hidden={isHidden} expandAll={props.expandAll} issues={issues}>
                <div className="config-option-container">
                    <Row>
                        {group.MultiProductOption
                            ? <ConfigExtras groupID={group.ID.toString()} />
                            : options}
                    </Row>
                </div>
        </ConfigGroupPanel>
    );
}

const Option = props => {

    const ctx = React.useContext(ConfiguratorContext)

    const option = props.option;
    const optionModel = ctx.config.GetOptionModel(option.ID);
    const [helpExpanded, setHelpExpanded] = React.useState(false);
    const helpID = React.useId();
    const toggleHelp = () => setHelpExpanded(value => !value);

    // Locked/IsVisible flip when OTHER options change (enable rules, dependencies),
    // so the wrapper must re-render on change events to show/hide itself — the
    // inner value controls subscribe for themselves but this class lives here.
    useChanged(option.ID.toString(), "OptionWrapper", false);

    // Runtime (show/hide engine) visibility: locked options flagged HideIfDisabled are hidden
    // rather than rendered read-only. Only re-renders on actual flips.
    const visibleForOrder = useVisibleForOrder(
        () => !isHiddenWhenDisabled(option, ctx.config.GetOptionModel(option.ID)),
        `Option_${option.ID}`);

    const isVisible = visibleForOrder && isOptionVisible(optionModel, ctx.filter);
    const readOnly = isReadOnly(optionModel);

    // To be able to hide the Group if all options are not visible, we need to report the visibility back to the parent Group
    React.useEffect(() => {
        props.visibility?.(option.ID, isVisible);
    }, [isVisible, option.ID, props.visibility]);

    const className = classNames("config-option", {
        "option-is-filtered": !isVisible,
        "readonly" : readOnly
    })

    return (
        <div className={className} data-config-field={option.ID.toString().toUpperCase()} tabIndex={-1}>
            <div className="config-option-header"><ConfigLabel option={props.option} helpExpanded={helpExpanded} onHelpToggle={toggleHelp} helpID={helpID}/></div>
            <div className="config-option-value-container">
                <OptionValue optionID={option?.ID?.toString()} displayType={optionModel.DisplayType} customDisplayType={option.CustomDisplayType}/>            
                <ActionBar optionID={option?.ID?.toString()}/>
            </div>
            {helpExpanded && <div className="config-option-help"><ConfigHelpNote id={helpID} notes={option.Notes} onHide={toggleHelp} /></div>}
        </div>
    );
};

export const OptionValue = props => {

    let ctrl = null;
    
    switch (props.displayType) {
        case optionDisplayTypes.Picklist:
            ctrl = <ConfigPicklist optionID={props.optionID} small={ props.small } />; break;
        case optionDisplayTypes.Numeric: ctrl = ctrl = <ConfigNumeric optionID={props.optionID} />; break;
        case optionDisplayTypes.Decimal: ctrl = ctrl = <ConfigDecimal optionID={props.optionID} />; break;
        case optionDisplayTypes.Checkbox:
            // Momentary command checkboxes (SWF import marker) render as a one-shot button.
            ctrl = props.customDisplayType === momentaryButtonDisplayType
                ? <ConfigActionButton optionID={props.optionID} />
                : <ConfigCheckbox optionID={props.optionID} />;
            break;
        case optionDisplayTypes.Length: ctrl = ctrl = <ConfigLength optionID={props.optionID} />; break;
        // Inline fabric options keep their own DisplayType in the runtime config but
        // render the SAME fabric pickers as library fabric options ("Custom" + control).
        case optionDisplayTypes.FabricInline:
            ctrl = <ConfigSingleLineFabric optionID={props.optionID} />; break;
        case optionDisplayTypes.Fabric2LineInline:
            ctrl = <ConfigTwoLineFabric optionID={props.optionID} small={props.small} />; break;
        case "Custom":

            if (props.customDisplayType === "SoftFurnishings.Controls.FabricOption2LineCtrl, SoftFurnishings") {
                ctrl = ctrl = <ConfigTwoLineFabric optionID={props.optionID} small={props.small} />; break;                
            } else if (props.customDisplayType === "SoftFurnishings.Controls.FabricOptionCtrl, SoftFurnishings") {
                ctrl = ctrl = <ConfigSingleLineFabric optionID={props.optionID} />; break;
            } else if (props.customDisplayType === "InsyteProductModule.Controls.PanelPatternOptionCtrl, InsyteProductModule") {
                ctrl = ctrl = <ConfigPanelPattern optionID={props.optionID} />; break;
            }

            break;
        default:
            ctrl = <ConfigText optionID={props.optionID} />;
            break;
    }

    const className = classNames("config-value", { "small": props.small });

    return (
        <div className={className} data-tagtname={props.optionID}>
            {ctrl}
        </div>
    );
};
